import React, { useEffect } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import SubHeaders from '@/components/Headers/SubHeaders'
import { IPermissionProps } from '@/types/shared'
import { paths } from '@/utils/path'
import { useTourHandler } from '@/contexts/tour-handler'
import {
    ClassificationSection,
    CustomerServiceSection,
    ContentSection,
    PriorityScheduleSection,
    AssigneesSection,
    ActionButtons,
} from './sections'
import { useCreateTicketForm } from './use-create-ticket-form'
import { useCreateTicketTour } from './use-create-ticket-tour'

const Create: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()

    const {
        formControl,
        control,
        handleSubmit,
        watch,
        setValue,
        isPending,
        slaInfo,
        onSubmit,
        handleServiceChange,
        handleServiceSLALoaded,
        handleChangeTicketTypeGroup,
        handleChangeTicketType,
    } = useCreateTicketForm()

    // Tour for Ticket Create form - force all fields to show
    const { startTour } = useCreateTicketTour({ setValue, watch })
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler when component mounts
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.ticket,
                        title: t('ticket-list'),
                    },
                    {
                        path: paths.ticketCreate,
                        title: t('ticket-create'),
                    },
                ]}
            />
            <FormProvider {...formControl}>
                <form className="mt-2" data-tour="ticket-form">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
                        {/* Left Column - Main Info */}
                        <div className="lg:col-span-8 space-y-2">
                            <ClassificationSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                onChangeTicketTypeGroup={
                                    handleChangeTicketTypeGroup
                                }
                                onChangeTicketType={handleChangeTicketType}
                            />

                            <CustomerServiceSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                onServiceChange={handleServiceChange}
                                onServiceSLALoaded={handleServiceSLALoaded}
                            />

                            <ContentSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                            />
                        </div>

                        {/* Right Column - Settings & Actions */}
                        <div className="lg:col-span-4 space-y-2">
                            <PriorityScheduleSection
                                control={control}
                                watch={watch}
                                setValue={setValue}
                                slaInfo={slaInfo}
                            />

                            <AssigneesSection control={control} />

                            <ActionButtons
                                onSubmit={handleSubmit(onSubmit)}
                                isPending={isPending}
                            />
                        </div>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default Create
